import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useGeolocation } from './useGeolocation';
import { useReverseGeocode } from './useReverseGeocode';

const CACHE_KEY = 'nonna_price_cache';
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const FAILURES_KEY = 'nonna_price_failures';
const MAX_FAILURES = 2;

interface FailureInfo {
  count: number;
  lastAttempt: number;
}

function loadFailures(): Record<string, FailureInfo> {
  try {
    return JSON.parse(localStorage.getItem(FAILURES_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveFailures(f: Record<string, FailureInfo>): void {
  try {
    localStorage.setItem(FAILURES_KEY, JSON.stringify(f));
  } catch {
    /* ignore */
  }
}

interface CachedPrice {
  price: number;
  productName: string | null;
  fetchedAt: number;
}

interface StoreInfo {
  name: string;
  address: string;
  locationId: string;
}

interface CacheData {
  version: number;
  storeInfo: StoreInfo | null;
  items: Record<string, CachedPrice>;
}

export interface UsePriceCacheReturn {
  // Get cached total for selected items (instant - no API call)
  getTotalForItems: (items: Set<string>) => { total: number; cachedCount: number; uncachedCount: number };
  // Get cached price for a single item
  getCachedPrice: (item: string) => CachedPrice | undefined;
  // Get store info
  storeInfo: StoreInfo | null;
  // Fetch prices for items not in cache
  fetchPricesForItems: (items: string[], force?: boolean) => Promise<void>;
  // Check if currently loading
  loading: boolean;
  // Number of items in cache
  cacheSize: number;
  // Clear the entire cache
  clearCache: () => void;
  // Progress tracking
  totalItemsToFetch: number;
  itemsFetched: number;
}

function loadCacheFromStorage(): CacheData {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.version === 1) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Failed to load price cache:', e);
  }
  return { version: 1, storeInfo: null, items: {} };
}

function saveCacheToStorage(cache: CacheData): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn('Failed to save price cache:', e);
  }
}

export function usePriceCache(): UsePriceCacheReturn {
  const { user } = useAuth();
  const [cache, setCache] = useState<CacheData>(() => loadCacheFromStorage());
  const [loading, setLoading] = useState(false);
  const [totalItemsToFetch, setTotalItemsToFetch] = useState(0);
  const [itemsFetched, setItemsFetched] = useState(0);
  const { latitude, longitude, requestLocation, permissionDenied } = useGeolocation();
  const { getZipFromCoords } = useReverseGeocode();
  const locationRequested = useRef(false);
  const fetchingRef = useRef<Set<string>>(new Set());
  const failuresRef = useRef<Record<string, FailureInfo>>(loadFailures());

  // Save cache to storage whenever it changes
  useEffect(() => {
    saveCacheToStorage(cache);
  }, [cache]);

  // Get items that need fetching (not in cache or expired, and not failed 2+ times recently)
  const getUncachedItems = useCallback((items: string[]): string[] => {
    const now = Date.now();
    return items.filter(item => {
      const key = item.toLowerCase();
      const cached = cache.items[key];
      if (!cached || (now - cached.fetchedAt) > TTL_MS) {
        const fail = failuresRef.current[key];
        if (fail && fail.count >= MAX_FAILURES && now - fail.lastAttempt <= TTL_MS) return false;
        return !fetchingRef.current.has(key);
      }
      return false;
    });
  }, [cache.items]);

  // Get total for selected items (instant - no API call)
  const getTotalForItems = useCallback((items: Set<string>) => {
    let total = 0;
    let cachedCount = 0;
    let uncachedCount = 0;
    
    items.forEach(item => {
      const key = item.toLowerCase();
      const cached = cache.items[key];
      if (cached && (Date.now() - cached.fetchedAt) <= TTL_MS) {
        total += cached.price;
        cachedCount++;
      } else {
        uncachedCount++;
      }
    });
    
    return { total, cachedCount, uncachedCount };
  }, [cache.items]);

  // Get cached price for a single item
  const getCachedPrice = useCallback((item: string): CachedPrice | undefined => {
    const key = item.toLowerCase();
    const cached = cache.items[key];
    if (cached && (Date.now() - cached.fetchedAt) <= TTL_MS) {
      return cached;
    }
    return undefined;
  }, [cache.items]);

  // Fetch prices for items not in cache - processes in batches for incremental progress
  const fetchPricesForItems = useCallback(async (items: string[], force = false) => {
    if (force) {
      items.forEach(i => delete failuresRef.current[i.toLowerCase()]);
      saveFailures(failuresRef.current);
    }
    const uncached = getUncachedItems(items);
    
    if (uncached.length === 0) {
      return; // All items already cached
    }

    // Mark items as being fetched to prevent duplicate requests
    uncached.forEach(item => fetchingRef.current.add(item.toLowerCase()));

    // Set progress tracking
    setTotalItemsToFetch(uncached.length);
    setItemsFetched(0);
    setLoading(true);

    try {
      let zipCode: string | null = null;
      let krogerLocationId: string | null = null;

      // Check user profile for saved location
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('zip_code, kroger_location_id')
          .eq('id', user.id)
          .single();

        if (profile?.zip_code) {
          zipCode = profile.zip_code;
          krogerLocationId = profile.kroger_location_id;
        }
      }

      // If no saved ZIP, try to detect location
      if (!zipCode) {
        let lat = latitude;
        let lon = longitude;

        if (!lat || !lon) {
          if (permissionDenied) {
            console.log('Location permission denied, skipping price fetch');
            return;
          }

          if (!locationRequested.current) {
            locationRequested.current = true;
            requestLocation();
          }

          // Check cached location
          const cachedLocation = localStorage.getItem('nonna_geolocation_cache');
          if (cachedLocation) {
            const parsed = JSON.parse(cachedLocation);
            lat = parsed.latitude;
            lon = parsed.longitude;
          }
        }

        if (lat && lon) {
          zipCode = await getZipFromCoords(lat, lon);
          
          // Save to profile if logged in
          if (zipCode && user) {
            await supabase
              .from('profiles')
              .update({ zip_code: zipCode })
              .eq('id', user.id);
          }
        }
      }

      if (!zipCode) {
        console.log('Unable to determine location for price fetch');
        return;
      }

      // Process in batches for incremental progress
      const BATCH_SIZE = 8;
      let fetchedCount = 0;

      for (let i = 0; i < uncached.length; i += BATCH_SIZE) {
        const batch = uncached.slice(i, i + BATCH_SIZE);

        let data: any = null;
        for (let attempt = 0; attempt < 2; attempt++) {
          const res = await supabase.functions.invoke('get-kroger-prices', {
            body: { items: batch, zip_code: zipCode, kroger_location_id: krogerLocationId },
          });
          if (!res.error && res.data) { data = res.data; break; }
          console.error('Error fetching prices for batch:', res.error);
        }

        // Record failures for items not priced in this batch
        const foundKeys = new Set<string>();
        (data?.items || []).forEach((item: any) => {
          if (item.found && item.price !== null) foundKeys.add(item.name.toLowerCase());
        });
        const now = Date.now();
        batch.forEach(name => {
          const key = name.toLowerCase();
          if (foundKeys.has(key)) {
            delete failuresRef.current[key];
          } else {
            const prev = failuresRef.current[key];
            const count = prev && now - prev.lastAttempt <= TTL_MS ? prev.count + 1 : 1;
            failuresRef.current[key] = { count, lastAttempt: now };
          }
        });
        saveFailures(failuresRef.current);

        fetchedCount += batch.length;
        setItemsFetched(fetchedCount);

        if (data?.items) {
          setCache(prev => {
            const newItems = { ...prev.items };
            data.items.forEach((item: any) => {
              const key = item.name.toLowerCase();
              if (item.found && item.price !== null) {
                newItems[key] = { price: item.price, productName: item.product_name, fetchedAt: now };
              }
            });
            const newStoreInfo = data.store_name ? {
              name: data.store_name,
              address: data.store_address || '',
              locationId: data.location_id || '',
            } : prev.storeInfo;
            return { ...prev, storeInfo: newStoreInfo, items: newItems };
          });
        }
      }

    } catch (err) {
      console.error('Error in fetchPricesForItems:', err);
    } finally {
      // Remove items from fetching set
      uncached.forEach(item => fetchingRef.current.delete(item.toLowerCase()));
      setLoading(false);
    }
  }, [user, latitude, longitude, requestLocation, permissionDenied, getZipFromCoords, getUncachedItems]);

  // Clear the entire cache
  const clearCache = useCallback(() => {
    setCache({ version: 1, storeInfo: null, items: {} });
    localStorage.removeItem(CACHE_KEY);
  }, []);

  return {
    getTotalForItems,
    getCachedPrice,
    storeInfo: cache.storeInfo,
    fetchPricesForItems,
    loading,
    cacheSize: Object.keys(cache.items).length,
    clearCache,
    totalItemsToFetch,
    itemsFetched,
  };
}
